import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Star } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

const blank = { name: "", rating: 5, text: "", location: "" };

export const ReviewsManager = () => {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = async () => {
    const { data } = await api.get("/admin/reviews");
    setReviews(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, rating: Math.min(5, Math.max(1, Number(form.rating) || 5)) };
      if (editing) await api.put(`/admin/reviews/${editing.id}`, payload);
      else await api.post("/admin/reviews", payload);
      toast.success(editing ? "Review updated" : "Review added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete review by ${r.name}?`)) return;
    await api.delete(`/admin/reviews/${r.id}`);
    toast.success("Review deleted");
    load();
  };

  return (
    <div data-testid="admin-reviews-manager">
      <div className="flex items-center justify-between gap-4">
        <h2 className="font-serif text-2xl text-cream">Reviews</h2>
        <Button data-testid="admin-add-review-btn" className="rounded-full bg-copper text-charcoal hover:bg-caramel"
          onClick={() => { setEditing(null); setForm(blank); setOpen(true); }}>
          <Plus className="mr-1.5 h-4 w-4" /> Add Review
        </Button>
      </div>

      <div className="mt-6 space-y-2">
        {reviews.map((r) => (
          <div key={r.id} data-testid={`admin-review-row-${r.id}`}
            className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-4">
            <div className="min-w-[200px] flex-1">
              <p className="text-sm font-semibold text-cream">{r.name} <span className="text-xs text-muted-foreground">· {r.location}</span></p>
              <p className="mt-1 text-xs text-cream/60">{r.text}</p>
            </div>
            <span className="flex items-center gap-1 text-sm text-caramel">
              <Star className="h-3.5 w-3.5 fill-caramel" /> {r.rating}
            </span>
            <Button size="icon" variant="ghost" data-testid={`admin-edit-review-${r.id}`}
              onClick={() => { setEditing(r); setForm({ name: r.name, rating: r.rating, text: r.text, location: r.location }); setOpen(true); }}>
              <Pencil className="h-4 w-4 text-cream/80" />
            </Button>
            <Button size="icon" variant="ghost" onClick={() => remove(r)} data-testid={`admin-delete-review-${r.id}`}>
              <Trash2 className="h-4 w-4 text-red-400" />
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="admin-review-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing ? "Edit Review" : "Add Review"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>Customer Name</Label>
              <Input value={form.name} required data-testid="review-name-input" className="mt-1.5"
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rating (1-5)</Label>
                <Input type="number" min={1} max={5} value={form.rating} data-testid="review-rating-input" className="mt-1.5"
                  onChange={(e) => setForm({ ...form, rating: e.target.value })} />
              </div>
              <div>
                <Label>Source</Label>
                <Input value={form.location} data-testid="review-location-input" className="mt-1.5"
                  onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Review</Label>
              <Textarea value={form.text} rows={3} data-testid="review-text-input" className="mt-1.5"
                onChange={(e) => setForm({ ...form, text: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="review-save-btn" className="rounded-full bg-copper text-charcoal hover:bg-caramel">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
